import { useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageStateGate } from "@/components/states";
import { PageTransition, StaggerContainer, StaggerItem } from "@/components/ui/page-transition";
import { Pulse, Gauge, X } from "@phosphor-icons/react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useUsageSummary } from "@/features/uso/hooks/useUsageSummary";
import {
  aggregateUsage,
  capabilityLabel,
  dayKeyFromIso,
  type UsageSummary,
} from "@/features/uso/lib/aggregate";
import { UsageHeaderCards } from "@/features/uso/components/UsageHeaderCards";
import { UsageBreakdownList } from "@/features/uso/components/UsageBreakdownList";
import { UsageTimeline } from "@/features/uso/components/UsageTimeline";
import { UsageBillingSplit } from "@/features/uso/components/UsageBillingSplit";
import type { UsageRange } from "@/lib/api/queryKeys";

const RANGES: Array<{ value: UsageRange; label: string }> = [
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "90d", label: "90 dias" },
  { value: "all", label: "Tudo" },
];

export default function UsoPage() {
  usePageTitle("Uso & Custos");
  const [range, setRange] = useState<UsageRange>("30d");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const summary = useUsageSummary(range);

  const displaySummary = useMemo<UsageSummary | undefined>(() => {
    if (!summary.data) return undefined;
    if (!selectedDate) return summary.data;
    const filtered = summary.data.rows.filter(
      (r) => dayKeyFromIso(r.created_at) === selectedDate,
    );
    return aggregateUsage(filtered);
  }, [summary.data, selectedDate]);

  const dateLabel = useMemo(() => {
    if (!selectedDate) return null;
    const [y, m, d] = selectedDate.split("-").map(Number);
    const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  }, [selectedDate]);

  return (
    <AppLayout>
      <PageStateGate
        empty={{
          icon: <Gauge weight="duotone" className="h-6 w-6" aria-hidden="true" />,
          title: "Sem uso registrado",
          description:
            "Quando você usar Roteiros, Consultas, Simulador ou Criativos, o consumo aparece aqui.",
        }}
      >
        <PageTransition className="space-y-6 w-full">
          <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]"
                />
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-medium">
                  Uso & Custos
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold text-foreground tracking-[-0.03em] leading-[1.05]">
                Seu consumo de IA
              </h1>
              <p className="text-sm text-muted-foreground max-w-xl">
                Tokens, custos e detalhamento por função. Valores em USD.
              </p>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-card/70 px-2 py-1 self-start">
              {RANGES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setRange(opt.value);
                    setSelectedDate(null);
                  }}
                  className={
                    opt.value === range
                      ? "rounded-md px-2.5 py-1 text-[11px] text-foreground bg-primary/15"
                      : "rounded-md px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </header>

          {summary.isLoading ? (
            <div className="text-sm text-muted-foreground py-12 text-center">
              <Pulse weight="duotone" className="h-5 w-5 mx-auto mb-2 animate-pulse" />
              Carregando uso…
            </div>
          ) : summary.isError ? (
            <div className="text-sm text-destructive py-12 text-center">
              Erro ao carregar uso.
            </div>
          ) : summary.data && summary.data.totals.callCount === 0 ? (
            <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-white/[0.08] rounded-2xl">
              Sem chamadas no período selecionado.
            </div>
          ) : displaySummary && summary.data ? (
            <StaggerContainer className="space-y-6">
              {selectedDate && (
                <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                  <p className="text-xs text-foreground">
                    Filtrado por <span className="font-semibold">{dateLabel}</span>
                    <span className="ml-2 text-muted-foreground">
                      · {displaySummary.totals.callCount} calls
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedDate(null)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                    Limpar
                  </button>
                </div>
              )}

              <StaggerItem>
                <UsageHeaderCards totals={displaySummary.totals} />
              </StaggerItem>

              <StaggerItem>
                <UsageTimeline
                  daily={summary.data.daily}
                  range={range}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              </StaggerItem>

              <StaggerItem>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <UsageBreakdownList
                    title="Por função"
                    groups={displaySummary.byCapability}
                    totalCost={displaySummary.totals.costUsd}
                    labelFor={capabilityLabel}
                  />
                  <UsageBreakdownList
                    title="Por modelo"
                    groups={displaySummary.byModel}
                    totalCost={displaySummary.totals.costUsd}
                  />
                </div>
              </StaggerItem>

              <StaggerItem>
                <UsageBillingSplit
                  platform={displaySummary.byBilling.platform}
                  user={displaySummary.byBilling.user}
                />
              </StaggerItem>
            </StaggerContainer>
          ) : null}
        </PageTransition>
      </PageStateGate>
    </AppLayout>
  );
}
